import { useRef, useState } from 'react'
import { ConvexError } from 'convex/values'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { parseRomeAppointment, romeAppointmentFields } from '../../../shared/deliveryAppointment'

export type PhaseRetryValues = {
    delivery_date: string
    delivery_time: string
    scansione_intraorale: boolean
}

export function PhaseRetryForm({ row, onSubmit, onCancel }: {
    row: { tipo_prova?: string; dataapp?: number; timeapp?: string; scansione_intraorale: boolean; presa_fisica_corriere: boolean }
    onSubmit: (values: PhaseRetryValues) => Promise<unknown>
    onCancel: () => void
}) {
    const initial = row.dataapp ? romeAppointmentFields(row.dataapp) : null
    const [date, setDate] = useState(row.dataapp && row.dataapp > Date.now() ? initial!.date : '')
    const [time, setTime] = useState(row.timeapp || initial?.time || '')
    const [mode, setMode] = useState(row.scansione_intraorale ? 'digital' : row.presa_fisica_corriere ? 'physical' : '')
    const [pending, setPending] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const submitting = useRef(false)

    return (
        <form className="mt-4 space-y-4 text-gray-900" aria-label="Riprova invio fase" onSubmit={async (event) => {
            event.preventDefault()
            if (submitting.current) return
            setError(null)
            try {
                parseRomeAppointment(date, time)
                if (!mode) throw new Error('Seleziona la modalità impronta.')
                submitting.current = true
                setPending(true)
                await onSubmit({ delivery_date: date, delivery_time: time, scansione_intraorale: mode === 'digital' })
            } catch (cause) {
                setError(cause instanceof ConvexError ? String(cause.data) : cause instanceof Error ? cause.message : 'Invio non avviato. Riprova tra poco.')
            } finally {
                submitting.current = false
                setPending(false)
            }
        }}>
            <p className="font-medium">Conferma i dati per la fase {row.tipo_prova || 'corrente'}</p>
            <p id="retry-delivery-hint" className="text-sm text-gray-700">Data, ora e modalità impronta sono obbligatorie. Orario italiano.</p>
            <fieldset disabled={pending} className="grid gap-4 sm:grid-cols-2" aria-describedby="retry-delivery-hint">
                <div className="min-w-0">
                    <label htmlFor="retry-delivery-date" className="mb-1 block font-medium">Data di consegna</label>
                    <Input autoFocus required type="date" id="retry-delivery-date" value={date} min={romeAppointmentFields(Date.now()).date}
                        className="bg-white text-base" onChange={(event) => setDate(event.target.value)} />
                </div>
                <div className="min-w-0">
                    <label htmlFor="retry-delivery-time" className="mb-1 block font-medium">Ora di consegna</label>
                    <Input required type="time" id="retry-delivery-time" value={time} className="bg-white text-base"
                        onChange={(event) => setTime(event.target.value)} />
                </div>
                <div className="min-w-0 sm:col-span-2">
                    <label htmlFor="retry-delivery-mode" className="mb-1 block font-medium">Modalità impronta</label>
                    <select required id="retry-delivery-mode" value={mode} onChange={(event) => setMode(event.target.value)}
                        className="min-h-[44px] w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-base focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring">
                        <option value="" disabled>Seleziona modalità</option>
                        <option value="physical">Impronta fisica / presa corriere</option>
                        <option value="digital">Digitale / scansione intraorale</option>
                    </select>
                </div>
            </fieldset>
            {error && <p role="alert" className="text-sm font-medium text-red-800">{error}</p>}
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button type="button" variant="outline" disabled={pending} onClick={onCancel}>Annulla</Button>
                <Button type="submit" disabled={pending}>{pending ? 'Invio in corso…' : 'Salva e riprova invio'}</Button>
            </div>
        </form>
    )
}
